import React, { useState, useMemo, useRef, useEffect } from "react";
import { UserStatsDto, UserStatsQueryDto } from "../models/user-stats-dto";
import "./UserStatsModal.css";

interface UserStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  statsData: UserStatsQueryDto | null;
}

type SortKey = keyof UserStatsDto;
type SortDirection = "asc" | "desc";

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

const UserStatsModal: React.FC<UserStatsModalProps> = ({
  isOpen,
  onClose,
  statsData,
}) => {
  const [activeTab, setActiveTab] = useState<"session" | "last30Days" | "allTime">("session");
  const [sessionSort, setSessionSort] = useState<SortConfig>({ key: "averageRelativePerformance", direction: "desc" });
  const [last30DaysSort, setLast30DaysSort] = useState<SortConfig>({ key: "averageRelativePerformance", direction: "desc" });
  const [allTimeSort, setAllTimeSort] = useState<SortConfig>({ key: "averageRelativePerformance", direction: "desc" });
  
  // Drag and resize state
  const [position, setPosition] = useState(() => {
    const centerX = (window.innerWidth - 1000) / 2;
    const centerY = (window.innerHeight - 600) / 2;
    return { x: Math.max(0, centerX), y: Math.max(0, centerY) };
  });
  const [size, setSize] = useState({ width: 1000, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Mouse event handlers for dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) { // Only start drag from header itself
      setIsDragging(true);
      setDragStart({ 
        x: e.clientX - position.x, 
        y: e.clientY - position.y 
      });
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        
        // Keep modal within viewport bounds
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
      
      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        const newWidth = Math.max(400, resizeStart.width + deltaX);
        const newHeight = Math.max(300, resizeStart.height + deltaY);
        
        // Ensure modal doesn't exceed viewport
        const maxWidth = window.innerWidth - position.x;
        const maxHeight = window.innerHeight - position.y;
        
        setSize({
          width: Math.min(newWidth, maxWidth),
          height: Math.min(newHeight, maxHeight)
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, position, size]);

  const handleSort = (key: SortKey) => {
    const currentSort = activeTab === "session" ? sessionSort : 
                       activeTab === "last30Days" ? last30DaysSort : allTimeSort;
    const setSort = activeTab === "session" ? setSessionSort : 
                   activeTab === "last30Days" ? setLast30DaysSort : setAllTimeSort;

    const direction = currentSort.key === key && currentSort.direction === "desc" ? "asc" : "desc";
    setSort({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!statsData) return { session: [], last30Days: [], allTime: [] };

    const sortArray = (data: UserStatsDto[], sortConfig: SortConfig) => {
      return [...data].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortConfig.direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      });
    };

    return {
      session: sortArray(statsData.session, sessionSort),
      last30Days: sortArray(statsData.last30Days, last30DaysSort),
      allTime: sortArray(statsData.allTime, allTimeSort),
    };
  }, [statsData, sessionSort, last30DaysSort, allTimeSort]);

  const renderSortIcon = (key: SortKey) => {
    const currentSort = activeTab === "session" ? sessionSort : 
                       activeTab === "last30Days" ? last30DaysSort : allTimeSort;
    
    if (currentSort.key !== key) return "↕️";
    return currentSort.direction === "asc" ? "↑" : "↓";
  };

  const formatNumber = (num: number, decimals: number = 2) => {
    return Number(num.toFixed(decimals));
  };

  const renderTable = (data: UserStatsDto[], isSessionTab: boolean = false) => {
    if (data.length === 0) {
      return <div className="no-data">No data available</div>;
    }

    if (isSessionTab) {
      // Session tab: only show Email, Total Cells, and Relative Performance
      return (
        <div className="table-wrapper">
          <div className="table-container">
            <table className="stats-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("email")} className="sortable">
                  Email {renderSortIcon("email")}
                </th>
                <th onClick={() => handleSort("totalCells")} className="sortable">
                  Total Cells {renderSortIcon("totalCells")}
                </th>
                <th onClick={() => handleSort("averageRelativePerformance")} className="sortable">
                  Relative Performance {renderSortIcon("averageRelativePerformance")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((user, index) => (
                <tr key={user.email} className={index % 2 === 0 ? "even" : "odd"}>
                  <td className="email-cell">{user.email}</td>
                  <td>{user.totalCells}</td>
                  <td className="performance-cell">
                    <span className={`performance-badge ${
                      user.averageRelativePerformance >= 1.2 ? "excellent" :
                      user.averageRelativePerformance >= 1.0 ? "good" :
                      user.averageRelativePerformance >= 0.8 ? "average" : "below-average"
                    }`}>
                      {formatNumber(user.averageRelativePerformance)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

    // All other tabs: show all columns
    return (
      <div className="table-wrapper">
        <div className="table-container">
          <table className="stats-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("email")} className="sortable">
                  Email {renderSortIcon("email")}
                </th>
                <th onClick={() => handleSort("totalCrosswords")} className="sortable">
                  Total Crosswords {renderSortIcon("totalCrosswords")}
                </th>
                <th onClick={() => handleSort("completedCrosswords")} className="sortable">
                  Completed {renderSortIcon("completedCrosswords")}
                </th>
                <th onClick={() => handleSort("totalCells")} className="sortable">
                  Total Cells {renderSortIcon("totalCells")}
                </th>
                <th onClick={() => handleSort("averageCells")} className="sortable">
                  Avg Cells {renderSortIcon("averageCells")}
                </th>
                <th onClick={() => handleSort("topPerformerCount")} className="sortable">
                  Top Performer {renderSortIcon("topPerformerCount")}
                </th>
                <th onClick={() => handleSort("averageRelativePerformance")} className="sortable">
                  Relative Performance {renderSortIcon("averageRelativePerformance")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((user, index) => (
                <tr key={user.email} className={index % 2 === 0 ? "even" : "odd"}>
                  <td className="email-cell">{user.email}</td>
                  <td>{user.totalCrosswords}</td>
                  <td>{user.completedCrosswords}</td>
                  <td>{user.totalCells}</td>
                  <td>{formatNumber(user.averageCells, 1)}</td>
                  <td>{user.topPerformerCount}</td>
                  <td className="performance-cell">
                    <span className={`performance-badge ${
                      user.averageRelativePerformance >= 1.2 ? "excellent" :
                      user.averageRelativePerformance >= 1.0 ? "good" :
                      user.averageRelativePerformance >= 0.8 ? "average" : "below-average"
                    }`}>
                      {formatNumber(user.averageRelativePerformance)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        ref={modalRef}
        className="modal-content" 
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        <div 
          className="modal-header" 
          onMouseDown={handleMouseDown}
          style={{ cursor: 'grab' }}
        >
          <h2>User Statistics</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === "session" ? "active" : ""}`}
              onClick={() => setActiveTab("session")}
            >
              Current Session
            </button>
            <button 
              className={`tab ${activeTab === "last30Days" ? "active" : ""}`}
              onClick={() => setActiveTab("last30Days")}
            >
              Last 30 Days
            </button>
            <button 
              className={`tab ${activeTab === "allTime" ? "active" : ""}`}
              onClick={() => setActiveTab("allTime")}
            >
              All Time
            </button>
          </div>
          
          <div className="tab-content">
            {activeTab === "session" && renderTable(sortedData.session, true)}
            {activeTab === "last30Days" && renderTable(sortedData.last30Days, false)}
            {activeTab === "allTime" && renderTable(sortedData.allTime, false)}
          </div>
        </div>
        
        <div 
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
        />
      </div>
    </div>
  );
};

export default UserStatsModal;